import React, { useMemo, useState } from "react";
import {
  AllCommunityModule,
  ModuleRegistry,
  ValidationModule,
} from "ag-grid-community";
// Core CSS
import "ag-grid-community/styles/ag-theme-quartz.css";
import "@/App.css";
import { AgGridReact } from "ag-grid-react";
import { parseUTCDate, DEFAULT_TIMEZONE } from "@/utils/dateHelpers";
import { formatFechaHoraEvento } from "@/lib/utilsUI";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ModalInformesFiscales from "../ModalInformesFiscales";

ModuleRegistry.registerModules([AllCommunityModule]);
if (import.meta.env.NODE_ENV !== "production") {
  // only include validation module in dev mode to reduce bundle size
  ModuleRegistry.registerModules([ValidationModule]);
}

export default function DataTableTicketsVendidos({
  statsTicketsVendidos = { data: [], pagination: {} },
  uidEvento,
}) {
  const [showModalInformes, setShowModalInformes] = useState(false);
  // Transform backend data to match the expected format
  const rowData = useMemo(() => {
    if (
      !statsTicketsVendidos?.data ||
      statsTicketsVendidos.data.length === 0
    ) {
      return [];
    }

    return statsTicketsVendidos.data.map((item, index) => ({
      id: index + 1,
      rut: item.numeroTributario || "N/A",
      nombre: item.userBuyerName || "N/A",
      edad: item.edad || "N/A",
      genero: item.genero || "N/A",
      instagram: item.instagram || "N/A",
      tipoTicket: item.ticketTypeName || "N/A",
      embajador: item.embajador || "No aplica",
      fechaCompra: item.fechaHoraCompra
        ? formatFechaHoraEvento(
            parseUTCDate(
              item.fechaHoraCompra,
              DEFAULT_TIMEZONE,
            )?.toJSDate(),
            DEFAULT_TIMEZONE,
          )
        : "N/A",
    }));
  }, [statsTicketsVendidos]);

  // Instagram link renderer
  const InstagramCellRenderer = (params) => {
    return (
      <a
        href={`https://instagram.com/${params.value}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-aw-green/85 hover:text-aw-green hover:underline"
      >
        {params.value}
      </a>
    );
  };

  // Column definitions
  const columnDefs = useMemo(
    () => [
      // RUT
      {
        headerName: "RUT",
        field: "rut",
        sortable: true,
        filter: "agNumberColumnFilter",
        filterParams: {
          filterOptions: ["equals", "greaterThan", "lessThan", "inRange"],
          defaultOption: "greaterThan",
        },
        width: 100,
        headerClass: "text-wrap",
        cellStyle: { color: "hsl(72, 80%, 55%)", textDecoration: "underline" },
      },
      // Nombre
      {
        headerName: "Nombre",
        field: "nombre",
        sortable: true,
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "startsWith",
        },
        width: 160,
        headerClass: "text-wrap",
        cellStyle: { color: "hsl(72, 80%, 55%)" },
      },
      // Edad
      {
        headerName: "Edad",
        field: "edad",
        sortable: true,
        filter: "agNumberColumnFilter",
        filterParams: {
          filterOptions: ["equals", "greaterThan", "lessThan", "inRange"],
          defaultOption: "greaterThan",
        },
        width: 85,
        headerClass: "text-wrap",
        cellStyle: { textAlign: "center" },
      },
      // Género
      {
        headerName: "Género",
        field: "genero",
        sortable: true,
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "startsWith",
        },
        width: 140,
        headerClass: "text-wrap",
        cellStyle: { textAlign: "center" },
      },
      // Instagram
      {
        headerName: "Instagram",
        field: "instagram",
        sortable: true,
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "startsWith",
        },
        cellRenderer: InstagramCellRenderer,
        maxWidth: 140,
        headerClass: "text-wrap",
      },
      // Tipo de Ticket
      {
        headerName: "Tipo de Ticket",
        field: "tipoTicket",
        sortable: true,
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "startsWith",
        },
        width: 140,
        headerClass: "text-wrap",
        cellStyle: { textAlign: "center" },
      },
      // Embajador
      {
        headerName: "Embajador",
        field: "embajador",
        sortable: true,
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "startsWith",
        },
        width: 140,
        cellStyle: { textAlign: "center", color: "hsl(72, 80%, 55%)" },
      },
      // Fecha de Compra
      {
        headerName: "Fecha de Compra",
        field: "fechaCompra",
        sortable: true,
        filter: "agTextColumnFilter",
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "startsWith",
        },
        width: 160,
        headerClass: "text-wrap",
        cellStyle: { textAlign: "center" },
      },
    ],
    [],
  );

  // Grid options
  const defaultColDef = useMemo(
    () => ({
      resizable: true,
      sortable: true,
      filter: true,
      floatingFilter: false,
    }),
    [],
  );

  const totalRecords = statsTicketsVendidos?.pagination?.totalRecords
    ?? statsTicketsVendidos?.data?.length
    ?? 0;

  return (
    <div className="w-full h-full">
      {/* Header */}
      <div className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-white">
              Tickets Vendidos
            </h2>
          </div>
          <button
            onClick={() => setShowModalInformes(true)}
            className="px-4 py-2 font-semibold bg-aw-green text-aw-bluedark rounded-md hover:scale-95 transition-transform flex items-center gap-2"
          >
            <FontAwesomeIcon icon={["fas", "file-excel"]} />
            Informe Fiscal
          </button>
        </div>
      </div>

      {/* Data Grid */}
      <div className="">
        <div className="ag-theme-quartz w-full" style={{ height: "600px" }}>
          <AgGridReact
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            pagination={true}
          />
        </div>
      </div>

      {/* Footer */}
      <div className="pt-3 flex items-center gap-4 text-sm text-gray-400">
        <span>
          Total de registros:{" "}
          <span className="text-white font-semibold">{totalRecords}</span>
        </span>
      </div>

      {/* Modal Informes Fiscales */}
      <ModalInformesFiscales
        isOpen={showModalInformes}
        onClose={() => setShowModalInformes(false)}
        uidEvento={uidEvento}
      />
    </div>
  );
}
